/**
 * Types for Institution Forms
 */

// API Response shape from backend (based on actual API documentation)
export interface InstitutionFormResponse {
  // Template information
  templateId?: number;
  templateName: string;

  // Upload information (present when form has been uploaded)
  uploadedFormId?: number;
  uploadedFileId?: number;

  // Inventory item association (for inventory-specific forms)
  inventoryItemId?: number;

  // Signatory information
  signatoryContactId?: number;
  signatoryName?: string;
  signatoryType?: 'LPR' | 'beneficiary' | 'other';

  // Status and metadata
  status: 'not_uploaded' | 'uploaded' | 'sent';
  requiresHardCopy: boolean;
  isFreeForm: boolean;

  // Form curation (share workflows)
  isCurated?: boolean;
  curationRules?: FormCurationRules | null;

  // Timestamps
  createdAt?: string;
  sentViaPostAt?: string;
  sentViaEmailAt?: string;
}

export interface FormCurationRules {
  instructions?: {
    sell?: boolean;
    transferToBeneficiary?: boolean;
    survivingJointHolder?: boolean;
  };
  crossBorderOnly?: boolean;
  intestacyOnly?: boolean;
  maxHoldingValue?: number;
}

// Frontend form interface for UI components (simplified for display)
export interface InstitutionForm {
  // Use templateId as the ID for non-uploaded forms, uploadedFormId for uploaded ones
  id: string | number;
  name: string;

  // Simplified status for UI
  status: 'not_uploaded' | 'uploaded' | 'sent';

  // Inventory item association (for inventory-specific forms)
  inventoryItemId?: number;

  // Signatory info
  signatoryName?: string;
  signatoryType?: 'LPR' | 'beneficiary' | 'other';
  signatoryContactId?: number;

  // Metadata
  requiresHardCopy: boolean;
  isFreeForm: boolean;

  // Form curation (share workflows)
  isCurated: boolean;
  curationRules?: FormCurationRules | null;

  // File references
  templateId?: number;
  uploadedFormId?: number;
  uploadedFileId?: number;

  // Timestamps
  createdAt?: string;
  sentViaPostAt?: string;
  sentViaEmailAt?: string;

  // Context (added by frontend)
  institutionId?: number;
  matterId?: number;
}

export interface InstitutionFormsListResponse {
  forms: InstitutionFormResponse[];
  total: number;
}

export interface InstitutionFormsFilters {
  institutionId?: number;
  status?: string;
  type?: string;
  limit?: number;
  offset?: number;
}

// Helper function to convert API response to frontend format
export function mapFormResponseToForm(
  response: InstitutionFormResponse,
  institutionId?: number,
  matterId?: number
): InstitutionForm {
  // Build a unique composite ID since the same template can appear multiple
  // times (e.g. per signatory or per inventory item)
  const baseId =
    response.templateId || response.uploadedFormId || `form-${Date.now()}`;
  const parts = [baseId];
  if (response.signatoryContactId != null)
    parts.push(`s${response.signatoryContactId}`);
  if (response.inventoryItemId != null)
    parts.push(`i${response.inventoryItemId}`);

  return {
    id: parts.join('-'),
    name: response.templateName,
    status: response.status,
    inventoryItemId: response.inventoryItemId,
    signatoryName: response.signatoryName,
    signatoryType: response.signatoryType,
    signatoryContactId: response.signatoryContactId,
    requiresHardCopy: response.requiresHardCopy,
    isFreeForm: response.isFreeForm,
    isCurated: response.isCurated ?? false,
    curationRules: response.curationRules ?? null,
    templateId: response.templateId,
    uploadedFormId: response.uploadedFormId,
    uploadedFileId: response.uploadedFileId,
    createdAt: response.createdAt,
    sentViaPostAt: response.sentViaPostAt,
    sentViaEmailAt: response.sentViaEmailAt,
    institutionId,
    matterId
  };
}
